// Store de autenticación con Zustand. Persiste tokens en localStorage.
//
// Tokens:
//   - access_token: corto (15 min por defecto).
//   - refresh_token: largo (30 días), permite renovar el access.
//
// El refresh_token debe almacenarse de forma segura; en una versión
// futura se moverá a cookies HttpOnly.SameSite=Strict para reducir la
// superficie de XSS. Por ahora localStorage + cuidado.

import { create } from 'zustand';

const ACCESS_KEY = 'camadero.access_token';
const REFRESH_KEY = 'camadero.refresh_token';
const USER_KEY = 'camadero.user';

export type Rol = 'admin' | 'operario' | 'ganadero' | 'veterinario';

export interface User {
  id: number;
  username: string;
  nombre: string | null;
  rol: Rol;
}

interface AuthState {
  user: User | null;
  // bump: cambia con cada setTokens/clearTokens para que los hooks
  // que dependen de tokens se re-rendericen.
  bump: number;
  setUser: (u: User | null) => void;
  setTokens: (access: string, refresh: string) => void;
  clearTokens: () => void;
  loadFromStorage: () => void;
}

function safeReadUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: safeReadUser(),
  bump: 0,
  setUser: (user) => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    set({ user });
  },
  setTokens: (access, refresh) => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    // Notificar a los hooks que dependen de tokens.
    set((s) => ({ bump: s.bump + 1 }));
  },
  clearTokens: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    set((s) => ({ bump: s.bump + 1, user: null }));
  },
  loadFromStorage: () => {
    set({ user: safeReadUser() });
  },
}));

/** Lee el access token desde localStorage (no reactivo). */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
/** Lee el refresh token desde localStorage (no reactivo). */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

/**
 * Helper externo para guardar tokens y notificar a los suscriptores.
 * Lo usa ``api.ts`` cuando el refresh automático genera un nuevo par.
 */
export function setTokens(access: string, refresh: string): void {
  useAuthStore.getState().setTokens(access, refresh);
}

/**
 * Helper externo para limpiar tokens.
 * Lo usa ``api.ts`` cuando el refresh falla.
 */
export function clearTokens(): void {
  useAuthStore.getState().clearTokens();
}

/** Hook reactivo al token actual. Se re-renderiza cuando cambia. */
export function useAccessToken(): string | null {
  useAuthStore((s) => s.bump);
  return getAccessToken();
}
